import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';
import _ from '../../../utils/lodash';
import { postingSchema } from '../../../utils/normalizr/postingSchema';
import { createPrimarySelector } from '../../../utils/helpers';
import { selectBands } from '../bands/selectors';

export const selectPostings = createPrimarySelector(state => state.entities.postings.general.entities.postings);
export const selectPostingsEntities = createPrimarySelector(state => state.entities.postings.general.entities);
export const selectPostingsOrder = createPrimarySelector(state => state.entities.postings.general.postingsOrder);
export const selectAvailabilities = createPrimarySelector(
  state => state.entities.postings.general.entities.availabilities,
);
export const selectClinicians = createPrimarySelector(state => state.entities.postings.general.entities.clinicians);
export const selectAssignments = createPrimarySelector(state => state.entities.postings.general.entities.assignments);
export const selectTravelTimes = createPrimarySelector(state => state.entities.postings.general.travelTimes);
export const selectPostingLocations = createPrimarySelector(state => state.entities.postings.locations.items);

export const selectSortedPostingsArray = createSelector(
  selectPostings,
  selectPostingsOrder,
  (postings, order) => (postings && order ? order.map(postingId => postings[postingId]) : null),
);

export const selectSortedPostingsWithBands = createSelector(
  selectSortedPostingsArray,
  selectBands,
  (postings, bands) => {
    if (!postings || !bands) return null;
    return postings.map(posting => ({
      ...posting,
      grade: bands[posting.gradeEquivalence],
    }));
  },
);

export const selectPostingsWithBands = createSelector(
  selectPostings,
  selectBands,
  (postings, bands) => {
    const newPostings = {};
    _.entries(postings).forEach(([key, value]) => {
      newPostings[key] = {
        ...value,
        grade: bands[value.gradeEquivalence],
      };
    });
    return newPostings;
  },
);

export const selectAssignmentsWithBands = createSelector(
  selectAssignments,
  selectBands,
  (assignments, bands) => {
    const newAssignments = {};
    _.entries(assignments).forEach(([key, value]) => {
      newAssignments[key] = {
        ...value,
        requiredGrade: bands[value.highestGradeEquivalence],
      };
    });
    return newAssignments;
  },
);

export const selectAvailabilitiesByClinicianId = (clinicianId, state) => {
  const clinicians = selectClinicians(state);
  const availabilities = selectAvailabilities(state);
  if (clinicians && clinicians[clinicianId] && availabilities) {
    return clinicians[clinicianId].availabilities.map(aviId => availabilities[aviId]);
  }
  return null;
};

export const selectAvailabilityById = (state, props) => {
  const availabilities = selectAvailabilities(state);
  return availabilities ? availabilities[props.availabilityId] : null;
};

export const selectAssignmentById = (state, props) => {
  const assignments = selectAssignmentsWithBands(state);
  return assignments ? assignments[props.assignGuid] : null;
};

export const selectAssignmentsWithTravelTime = createSelector(
  selectAssignmentsWithBands,
  selectTravelTimes,
  (assignments, travelTimes) => {
    const newAssignments = {};
    _.entries(assignments).forEach(([key, value]) => {
      newAssignments[key] = {
        ...value,
        travelTime: travelTimes[value.postcode],
      };
    });
    return newAssignments;
  },
);

export const selectAssignmentsArrayByAvailabilityId = createSelector(
  selectAvailabilityById,
  selectAssignmentsWithTravelTime,
  (availability, assignments) => {
    return availability ? availability.assignments.map(assignGuid => assignments[assignGuid]) : [];
  },
);

// Used for memoization since it works on props
export const makeSortedAssignmentsArraySelector = () =>
  createSelector(
    selectAssignmentsArrayByAvailabilityId,
    assignments => _.sortBy(assignments, 'visualStartDate'),
  );

export const selectDenormalizedPostings = createSelector(
  selectPostingsEntities,
  selectPostingsWithBands,
  selectAssignmentsWithBands,
  selectPostingsOrder,
  (entities, postings, assignments, postingsOrder) =>
    denormalize(postingsOrder, [postingSchema], {
      ...entities,
      postings,
      assignments,
    }),
);

export const selectPostingLocationsWithBands = createSelector(
  selectPostingLocations,
  selectBands,
  (postingLocations, bands) =>
    postingLocations.map(postingLocation => ({
      ...postingLocation,
      grade: bands[postingLocation.gradeEquivalence],
    })),
);
